import { useSearchParams } from "react-router-dom";
import { useCallback } from "react";
import { SortOrder } from "../Constants/tableTypes";

export interface TableParamsOptions<TSortColumn extends string> {
  defaultSortColumn?: TSortColumn;
  defaultSortOrder?: SortOrder;
  defaultPage?: number;
  defaultSearch?: string;
  defaultPageSize?: number;
}

export const useTableParams = <TSortColumn extends string = string>(
  options: TableParamsOptions<TSortColumn> = {}
) => {
  const {
    defaultSortColumn = "",
    defaultSortOrder = "asc",
    defaultPage = 1,
    defaultSearch = "",
    defaultPageSize = 10,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || defaultPage);
  const search = searchParams.get("search") || defaultSearch;
  const pageSize = Number(searchParams.get("pageSize") || defaultPageSize);
  const sortColumn =
    (searchParams.get("sortColumn") as TSortColumn) || defaultSortColumn;
  const sortOrder =
    (searchParams.get("sortOrder") as SortOrder) || defaultSortOrder;

  const updateParams = useCallback(
    (params: Partial<Record<string, string>>) => {
      const newParams: Record<string, string> = {};

      const p = params.page ?? page.toString();
      if (p) newParams.page = p;

      const s = params.search ?? search;
      if (s) newParams.search = s;

      const sc = params.sortColumn ?? sortColumn;
      if (sc) newParams.sortColumn = sc;

      const so = params.sortOrder ?? sortOrder;
      if (so) newParams.sortOrder = so;

      const ps = params.pageSize ?? pageSize.toString();
      if (ps) newParams.pageSize = ps;

      setSearchParams(newParams);
    },
    [page, search, sortColumn, sortOrder, pageSize, setSearchParams]
  );

  const setPage = (newPage: number) => updateParams({ page: String(newPage) });
  const setSearch = (value: string) =>
    updateParams({ search: value, page: "1" });
  const setSort = (column: TSortColumn) => {
    const newOrder =
      column === sortColumn && sortOrder === "asc" ? "desc" : "asc";
    updateParams({ sortColumn: column, sortOrder: newOrder, page: "1" });
  };
  const setPageSize = (newSize: number) =>
    updateParams({ pageSize: String(newSize), page: "1" });

  return {
    page,
    search,
    pageSize,
    sortColumn,
    sortOrder,
    setPage,
    setSearch,
    setSort,
    setPageSize,
  };
};
